import { useEffect, useRef, useState } from 'preact/hooks';
import {
  getFirstRun,
  listCommsConversations,
  type CommsConversation,
  type FirstRunLane,
} from '../lib/api';
import { routePath, type ChatLane } from '../routes/route';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { ChatThread } from '../components/ChatThread';
import { ListSearch, matches } from '../components/ListSearch';
import { ListSort, type SortDir } from '../components/ListSort';
import { useFrameOwnsBack } from '../components/FrameBack';
import { useMarkThreadRead } from '../hooks/useUnread';
import { publishThreadUnread, threadKey, unreadLabel } from '../lib/unread';
import './TechChat.css';

// CV2 — THE CHAT TAB BECOMES REAL (decision 8a36f2de).
//
// S5b built this screen as a door to Telegram, and its header carried the
// three reasons a tech could not chat in-app — the decisive one being that
// core's `laneVisibleTo` granted a tech nothing. CV1 changed that fact at
// the engine (memberVisibility.ts: derived ∪ granted job lanes, own two DM
// lanes, member sends), so the reasoning that built the door screen is
// retired WITH the fact, and this is now the room itself:
//
//   · THE LIST is `GET /api/comms/conversations` answering a member
//     honestly — their job threads (derived ∪ granted) and their own two
//     direct lanes, MRU. One read; no per-row fan-out (the request budget
//     suite holds the ceiling). A `pending`/`failed` topic says the thread
//     is not live yet rather than hiding.
//   · SEARCH + SORT (recent | name): this list is the tech's whole
//     conversational world and must stay findable at 20+ assigned jobs.
//   · THE THREAD is the shared ChatThread (newest-first, scroll back,
//     member sends; refusals in the server's own words — the §1 gate's 403
//     and the no-live-thread 422 both render verbatim).
//
// TELEGRAM IS DEMOTED, NOT REMOVED (the migration posture): a DM thread
// offers a quiet "Open in Telegram" line using the same `/api/first-run`
// lane deep-links the old door screen used — fetched only when a DM thread
// opens, so the list stays one read. No QR, no instruction, no fabricated
// URL when the deployment has no bot username.

export interface TechChatProps {
  tenantId: string;
  /** Which conversation is open, from the route. Absent = the list. */
  open?: { lane: ChatLane; id: string };
  onNavigate: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

/** One page. A tech's world is tens of conversations, not thousands — and
 * the page after this one is a button, never a sweep. */
const PAGE = 50;

function laneLabel(lane: 'office' | 'dispatch'): string {
  return lane === 'office' ? 'Office' : 'Dispatch';
}

function keyOf(c: CommsConversation): string {
  return c.kind === 'job' ? threadKey('job', c.recordId) : threadKey(c.lane, c.membershipId);
}

function titleOf(c: CommsConversation): string {
  // The member shape needs no roster: a job is its record's name, and a DM
  // lane is the standing line to the office/dispatch — the counterpart IS
  // the title.
  return c.kind === 'job' ? c.recordName : laneLabel(c.lane);
}

function mark(c: CommsConversation): string {
  if (c.kind === 'dm') return c.lane === 'office' ? 'OF' : 'DI';
  const digits = c.recordName.replace(/\D+/g, '');
  if (digits.length >= 3) return digits.slice(-4);
  return c.recordName.slice(0, 2).toUpperCase();
}

function when(iso: string, now: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameDay =
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  ) {
    return 'Yest';
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const SORTS = [
  { key: 'recent', label: 'Recent', ascWord: 'oldest first', descWord: 'newest first', defaultDir: 'desc' as SortDir },
  { key: 'name', label: 'Name', ascWord: 'A–Z', descWord: 'Z–A', defaultDir: 'asc' as SortDir },
] as const;

export function TechChat({ tenantId, open, onNavigate }: TechChatProps) {
  // CV5 §2 — see the thread's head below.
  const frameOwnsBack = useFrameOwnsBack();
  const [load, setLoad] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [conversations, setConversations] = useState<CommsConversation[]>([]);
  const [maybeMore, setMaybeMore] = useState(false);
  const [paging, setPaging] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<string>('recent');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  /** The Telegram door's deep links (`/api/first-run` lane rows) — read
   * ONLY when a DM thread is open, cached for the session. Null = not read
   * (or refused); the door simply doesn't render then. */
  const [telegramLanes, setTelegramLanes] = useState<FirstRunLane[] | null>(null);

  const now = new Date();
  const openKey = open ? `${open.lane}:${open.id}` : null;
  const openIsDm = open !== undefined && open.lane !== 'job';

  // THE LIST READ, and when it happens again. The route renders this one
  // component for the list AND a thread, so navigating back from a thread
  // does not remount it — and a mount-only read left the list FROZEN at
  // its first answer (the CV2 walk caught it: a job granted while a thread
  // was open never appeared on return). So: read at mount (list or
  // deep-linked thread — the thread's heading and topic notice come off
  // this list), read AGAIN each time the tech returns to the list, and
  // deliberately NOT on list→thread (the thread pages its own messages;
  // re-reading the list under it would buy nothing). The per-mount request
  // budget is unchanged: one list read, still no per-row fan-out.
  const listReadFor = useRef<string | null>(null);
  useEffect(() => {
    const world = `${tenantId}:${retryNonce}`;
    if (open !== undefined && listReadFor.current === world) return;
    listReadFor.current = world;
    let cancelled = false;
    setLoad((prev) => (prev === 'ready' ? 'ready' : 'loading'));
    setFailure(null);
    listCommsConversations(tenantId, { limit: PAGE, offset: 0 })
      .then((page) => {
        if (cancelled) return;
        setConversations(page);
        setMaybeMore(page.length === PAGE);
        setLoad('ready');
        // CV6b §4 — the tab badge and the installed icon read from the same
        // answer this list renders. Published here rather than computed in
        // the shell so there is ONE source for "what is new": the server's.
        publishThreadUnread(
          page.map((c) => ({ key: keyOf(c), count: c.unreadCount, capped: c.unreadCountCapped }))
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLoad('error');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, retryNonce, openKey]);

  // The familiar door, kept: the DM thread's Open-in-Telegram line needs
  // the per-bot deep link, and `/api/first-run` already answers it (the
  // S5b reasoning for that source, unchanged). Read once, on first DM open.
  useEffect(() => {
    if (!openIsDm || telegramLanes !== null) return;
    let cancelled = false;
    getFirstRun(tenantId)
      .then((state) => {
        if (!cancelled) setTelegramLanes(state.lanes);
      })
      .catch(() => {
        // No door, no error: the thread itself is whole without it.
      });
    return () => {
      cancelled = true;
    };
  }, [openIsDm, tenantId, telegramLanes]);

  // CV6b §4 — opening a thread marks it read (the hook owns the one call and
  // the badge); the row it came from zeroes here, so returning to the list
  // shows the truth without a second read.
  useMarkThreadRead(tenantId, open, (thread) => {
    setConversations((prev) =>
      prev.map((c) => (keyOf(c) === thread ? { ...c, unreadCount: 0, unreadCountCapped: false } : c))
    );
  });

  async function loadNextPage() {
    if (paging) return;
    setPaging(true);
    try {
      const next = await listCommsConversations(tenantId, { limit: PAGE, offset: conversations.length });
      const merged = [...conversations, ...next];
      setConversations(merged);
      setMaybeMore(next.length === PAGE);
      // Republish over the WHOLE loaded list: publishThreadUnread replaces
      // rather than merges (see its header), so a page-2 publish carrying
      // only page 2 would drop page 1's counts off the badge.
      publishThreadUnread(
        merged.map((c) => ({ key: keyOf(c), count: c.unreadCount, capped: c.unreadCountCapped }))
      );
    } catch (err) {
      setFailure(describeReadFailure(err));
    }
    setPaging(false);
  }

  if (load === 'loading') {
    return (
      <div class="tchat">
        <div class="tchat__eyebrow">Chat</div>
        <div class="tchat__say">One moment…</div>
      </div>
    );
  }

  if (load === 'error' && failure !== null) {
    return (
      <div class="tchat">
        <div class="tchat__eyebrow">Chat</div>
        <div class="tchat__say" data-testid="tchat-failure">
          {failure.headline}
          <br />
          <span class="tchat__soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="tchat__retry" onClick={() => setRetryNonce((n) => n + 1)}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  /* ---------------- one conversation ---------------- */
  if (open) {
    const conversation = conversations.find((c) => keyOf(c) === openKey) ?? null;
    const heading = conversation
      ? titleOf(conversation)
      : open.lane === 'job'
        ? 'Job thread'
        : laneLabel(open.lane);
    // A topic that is not live yet is said out loud — the send would 422
    // with the server's words anyway; this names it before the first try.
    const notice =
      conversation?.kind === 'job' && conversation.topicStatus !== 'created'
        ? `This job's thread isn't live yet — it is ${conversation.topicStatus}. What you send here can't go through until it is.`
        : null;

    const doorLane =
      openIsDm && telegramLanes !== null
        ? (telegramLanes.find((l) => l.key === open.lane) ?? null)
        : null;

    return (
      <div class="tchat tchat--thread">
        <div class="tchat__thead">
          {/* CV5 §2 — one escape, one place: the frame renders the way back
              on the phone (the conversation route's parent IS `chat`), so
              this stops drawing a second one. Unchanged everywhere the frame
              provides none. */}
          {frameOwnsBack ? null : (
            <button
              type="button"
              class="tchat__back"
              onClick={() => onNavigate(routePath({ screen: 'chat' }))}
            >
              ‹ Chat
            </button>
          )}
          <div class="tchat__thead-tx">
            <b>{heading}</b>
            <small>{open.lane === 'job' ? 'Job thread' : `${laneLabel(open.lane)} lane`}</small>
          </div>
        </div>
        <ChatThread
          tenantId={tenantId}
          lane={open.lane}
          id={open.id}
          viewerKind="tech"
          notice={notice}
          extra={
            doorLane?.deepLink ? (
              // Demoted, not removed: the familiar door, one quiet line.
              <a
                class="tchat__tg"
                href={doorLane.deepLink}
                target="_blank"
                rel="noreferrer"
                data-testid="tchat-telegram-door"
              >
                Open in Telegram
              </a>
            ) : null
          }
        />
      </div>
    );
  }

  /* ---------------- the list ---------------- */
  const shown = conversations.filter((c) => matches(query, [titleOf(c)]));
  if (sortKey === 'name') {
    shown.sort((a, b) => titleOf(a).localeCompare(titleOf(b)) * (sortDir === 'asc' ? 1 : -1));
  } else if (sortDir === 'asc') {
    shown.reverse();
  }

  return (
    <div class="tchat">
      <div class="tchat__eyebrow">Chat</div>
      <ListSearch
        value={query}
        onChange={setQuery}
        placeholder="Find a job or a lane"
        label="Search conversations"
      />
      <ListSort
        options={SORTS}
        activeKey={sortKey}
        dir={sortDir}
        onChange={(key, dir) => {
          setSortKey(key);
          setSortDir(dir);
        }}
      />
      <div class="tchat__list" data-testid="tchat-list">
        {conversations.length === 0 ? (
          <div class="tchat__none">
            Nothing here yet. Your jobs' threads and the office's line to you land on this screen.
          </div>
        ) : shown.length === 0 ? (
          <div class="tchat__none" data-testid="tchat-no-match">
            Nothing matches “{query}”.
          </div>
        ) : (
          shown.map((c) => {
            const last = c.lastMessage;
            // CV6b §4 — WHAT IS NEW, on the row. MRU ordering is untouched
            // (the spec's own instruction: do not invent a second sort) —
            // the mark is what makes an unread thread findable, not a
            // reshuffle that would move a conversation out from under a
            // thumb already reaching for it.
            const unread = c.unreadCount > 0;
            return (
              <button
                type="button"
                class={`tchat__row${unread ? ' tchat__row--unread' : ''}`}
                key={keyOf(c)}
                onClick={() =>
                  onNavigate(
                    routePath({
                      screen: 'conversation',
                      ...(c.kind === 'job'
                        ? { lane: 'job' as const, id: c.recordId }
                        : { lane: c.lane, id: c.membershipId }),
                    })
                  )
                }
              >
                <span class="tchat__mark2" aria-hidden="true">
                  {mark(c)}
                </span>
                <span class="tchat__row-tx">
                  <b>{titleOf(c)}</b>
                  <small>
                    {c.kind === 'job' && c.topicStatus !== 'created'
                      ? // HONEST, not hidden: the thread exists as a fact of
                        // the record, and its wire is not up yet.
                        `Thread isn't live yet — ${c.topicStatus}`
                      : last
                        ? `${last.senderKind === 'system' ? 'Rexx' : last.senderKind === 'office' ? 'Office' : 'Tech'}: ${last.body}`
                        : 'Nothing said yet'}
                  </small>
                </span>
                <span class="tchat__row-t">
                  {when(c.lastActivityAt, now)}
                  {unread ? (
                    <span class="tchat__unread" data-testid={`tchat-unread-${keyOf(c)}`}>
                      {unreadLabel({ count: c.unreadCount, capped: c.unreadCountCapped })}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })
        )}
        {maybeMore ? (
          <button type="button" class="tchat__retry" disabled={paging} onClick={() => void loadNextPage()}>
            {paging ? 'One moment…' : 'More conversations'}
          </button>
        ) : null}
      </div>
    </div>
  );
}
